import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { LeadFormInput, LeadFormPrefsInput } from "@sales-ops-amplify/shared";
import {
  createLeadForm,
  deleteLeadForm,
  getFormEmbed,
  getFormPreferences,
  getLeadForm,
  listFormSubmissions,
  listLeadForms,
  publishLeadForm,
  updateFormPreferences,
  updateLeadForm,
} from "@/api/forms";

export function useLeadForms() {
  return useQuery({
    queryKey: ["forms"],
    queryFn: listLeadForms,
  });
}

export function useLeadForm(formId: string | undefined) {
  return useQuery({
    queryKey: ["forms", formId],
    queryFn: () => getLeadForm(formId!),
    enabled: Boolean(formId),
  });
}

export function useFormSubmissions(formId: string | undefined) {
  return useQuery({
    queryKey: ["forms", formId, "submissions"],
    queryFn: () => listFormSubmissions(formId!),
    enabled: Boolean(formId),
  });
}

export function useFormEmbed(formId: string | undefined) {
  return useQuery({
    queryKey: ["forms", formId, "embed"],
    queryFn: () => getFormEmbed(formId!),
    enabled: Boolean(formId),
  });
}

export function useFormPreferences() {
  return useQuery({
    queryKey: ["forms", "preferences"],
    queryFn: getFormPreferences,
  });
}

export function useCreateLeadForm() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: LeadFormInput) => createLeadForm(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["forms"] });
    },
  });
}

export function useUpdateLeadForm() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      formId,
      patch,
    }: {
      formId: string;
      patch: LeadFormInput;
    }) => updateLeadForm(formId, patch),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ["forms"] });
      qc.invalidateQueries({ queryKey: ["forms", vars.formId] });
    },
  });
}

export function usePublishLeadForm() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      formId,
      domains,
    }: {
      formId: string;
      domains?: string[];
    }) => publishLeadForm(formId, { domains }),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ["forms"] });
      qc.invalidateQueries({ queryKey: ["forms", vars.formId] });
    },
  });
}

export function useDeleteLeadForm() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (formId: string) => deleteLeadForm(formId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["forms"] });
    },
  });
}

export function useUpdateFormPreferences() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: LeadFormPrefsInput) => updateFormPreferences(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["forms", "preferences"] });
    },
  });
}
